import FJUtil from '../../../../util/FJUtil';

// mask 画布
class FJMaskCanvas {
    canvas = null;
    ctx = null;

    _loaded = false;
    _isDrawing = false;
    _lastPosition = null;

    constructor() {
        this.canvas = new OffscreenCanvas(0, 0);
        // 获取上下文时设置 willReadFrequently 属性
        this.ctx = this.canvas.getContext('2d', { willReadFrequently: true });
    }

    init(size) {
        if (!size || !size.width || !size.height) return;
        this.canvas.width = size.width;
        this.canvas.height = size.height;
        this._loaded = true;
    }

    startDrawing(position) {
        this._lastPosition = position;
        this._isDrawing = true;
    }

    stopDrawing() {
        this._isDrawing = false;
    }

    draw(context, rect) {
        if (!this._loaded) return;
        context.drawImage(this.canvas, rect.x, rect.y, rect.width, rect.height);
    }

    async drawByImageUrl(imageUrl) {
        if (!this._loaded) return false;

        try {
            await new Promise((resolve, reject) => {
                const image = new Image();
                image.src = imageUrl;
                image.onload = () => {
                    if (image.width !== this.canvas.width || image.height !== this.canvas.height) return;

                    const canvas = document.createElement('canvas');
                    canvas.width = image.width;
                    canvas.height = image.height;
                    const ctx = canvas.getContext('2d');
                    ctx.drawImage(image, 0, 0);
                    let maskData = ctx.getImageData(0, 0, this.canvas.width, this.canvas.height);
                    const imageData = new ImageData(this.canvas.width, this.canvas.height);
                    for (let i = 0; i < maskData.data.length; i += 4) {
                        const r = maskData.data[i];
                        if (r === 0) {
                            imageData.data[i] = 0; // R
                            imageData.data[i + 1] = 0; // G
                            imageData.data[i + 2] = 0; // B
                            imageData.data[i + 3] = 0; // A
                        } else {
                            imageData.data[i] = 0; // R
                            imageData.data[i + 1] = 0; // G·
                            imageData.data[i + 2] = 255; // B
                            imageData.data[i + 3] = 100; // A
                        }
                    }

                    this.setMaskData(imageData);
                    resolve(canvas);
                };

                image.onerror = reject;
            });

            return true;
        } catch (error) {
            return false;
        }
    }

    /**
     * @description 获取更新像素的参数
     * @param {{x: number, y: number}} startPoint 开始点
     * @param {{x: number, y: number}} endPoint 结束点
     * @param {number} brushSize 画笔大小
     * @param {boolean} isErase 是否是擦除
     */
    _getUpdatePixelOptions(startPoint, endPoint, brushSize, isErase) {
        // 1. 获取更新范围的大小
        let minX = Math.min(startPoint.x, endPoint.x);
        let maxX = Math.max(startPoint.x, endPoint.x);
        let minY = Math.min(startPoint.y, endPoint.y);
        let maxY = Math.max(startPoint.y, endPoint.y);

        let rect = {
            x: minX - brushSize / 2,
            y: minY - brushSize / 2,
            width: Math.round(maxX - minX + brushSize),
            height: Math.round(maxY - minY + brushSize),
        };

        // 2. 获取起始点和结束点的角度
        const angle = FJUtil.angleAtPoint(endPoint, startPoint);

        return {
            rect,
            angle,
            brushSize,
            isErase,
            startPoint,
            endPoint,
        };
    }

    /**
     * @description 更新像素到渲染画布
     * @param {{x: number, y: number, width: number, height: number}} options.rect  更新范围的大小
     * @param {{x: number, y: number}} options.startPoint 开始点
     * @param {{x: number, y: number}} options.endPoint 结束点
     * @param {number} options.angle 角度
     * @param {number} options.brushSize 画笔大小 (直径)
     * @param {boolean} options.isErase 是否是擦除
     * @param {function} addUndo 添加撤销
     */
    _updatePixelToRenderCanvasFromData(options, addUndo) {
        const { rect, angle, startPoint, endPoint, brushSize, isErase } = options;

        const imgData = this.ctx.getImageData(rect.x, rect.y, rect.width, rect.height);

        const endPointAngleAfterPoint = FJUtil.convertPointByAngle(endPoint, startPoint, angle);

        let opacity = 100;
        const hardness = 0.6;

        const __getOpacity = distance => {
            const radius = brushSize / 2;
            if (isErase) {
                return 100 - (1 - Math.max(distance - radius * hardness, 0) / (radius * (1 - hardness))) * 100;
            } else {
                return (1 - Math.max(distance - radius * hardness, 0) / (radius * (1 - hardness))) * 100;
            }
        };

        let isUpdateView = false;
        for (let y = 0; y < rect.height; y++) {
            for (let x = 0; x < rect.width; x++) {
                const pixelPoint = { x: x + rect.x, y: y + rect.y };
                const pixelAngleAfterPoint = FJUtil.convertPointByAngle(pixelPoint, startPoint, angle);

                const isBetween =
                    pixelAngleAfterPoint.x >= startPoint.x &&
                    pixelAngleAfterPoint.x <= endPointAngleAfterPoint.x &&
                    pixelAngleAfterPoint.y >= startPoint.y - brushSize / 2 &&
                    pixelAngleAfterPoint.y <= endPointAngleAfterPoint.y + brushSize / 2;

                const isInStartRound =
                    FJUtil.getDistanceBetweenPoints(pixelAngleAfterPoint, startPoint) <= brushSize / 2;

                const isInEndRound =
                    FJUtil.getDistanceBetweenPoints(pixelAngleAfterPoint, endPointAngleAfterPoint) <= brushSize / 2;

                if (isInStartRound) {
                    const distanceToStart = FJUtil.getDistanceBetweenPoints(pixelAngleAfterPoint, startPoint);
                    opacity = __getOpacity(distanceToStart);
                }

                if (isInEndRound) {
                    const distanceToEnd = FJUtil.getDistanceBetweenPoints(
                        pixelAngleAfterPoint,
                        endPointAngleAfterPoint,
                    );
                    opacity = __getOpacity(distanceToEnd);
                }

                if (isBetween) {
                    const distanceToY = Math.abs(pixelAngleAfterPoint.y - startPoint.y);
                    opacity = __getOpacity(distanceToY);
                }

                if (isInEndRound || isBetween || isInStartRound) {
                    isUpdateView = true;
                    const index = (y * rect.width + x) * 4;
                    if (isErase) {
                        imgData.data[index + 3] = opacity < imgData.data[index + 3] ? opacity : imgData.data[index + 3]; // A
                    } else {
                        imgData.data[index] = 0; // R
                        imgData.data[index + 1] = 0; // G
                        imgData.data[index + 2] = 255; // B
                        imgData.data[index + 3] = opacity > imgData.data[index + 3] ? opacity : imgData.data[index + 3]; // A
                    }
                }
            }
        }

        if (isUpdateView) {
            addUndo && addUndo();
            this.ctx.putImageData(imgData, rect.x, rect.y);
        }

        return isUpdateView;
    }

    /**
     * @description 检查是否在画布内
     * @param {{x: number, y: number, width: number, height: number}} rect 范围
     */
    _checkInCanvas(rect) {
        const size = this.getSize();
        return !(
            (rect.x < 0 && rect.x + rect.width < 0) ||
            rect.x > size.width ||
            (rect.y < 0 && rect.y + rect.height < 0) ||
            rect.y > size.height
        );
    }

    /**
     * @desc 绘制处理
     * @param {{x: number, y: number}} startPoint 开始点
     * @param {{x: number, y: number}} endPoint 结束点
     * @param {function} addUndo 添加撤销
     * @param {number} brushSize 画笔大小
     */
    drawBrush(startPoint, endPoint, brushSize, addUndo) {
        if (!this._isDrawing) return;

        const options = this._getUpdatePixelOptions(startPoint, endPoint, brushSize, false);

        const isInCanvas = this._checkInCanvas(options.rect);
        if (isInCanvas) {
            return this._updatePixelToRenderCanvasFromData(options, addUndo);
        }
        return false;
    }

    /**
     * @desc 擦除处理
     * @param {{x: number, y: number}} point 点
     * @param {number} size 大小
     * @param {function} addUndo 添加撤销
     */
    eraseBrush(startPoint, endPoint, brushSize, addUndo) {
        const options = this._getUpdatePixelOptions(startPoint, endPoint, brushSize, true);
        const isInCanvas = this._checkInCanvas(options.rect);
        if (isInCanvas) {
            return this._updatePixelToRenderCanvasFromData(options, addUndo);
        }
        return false;
    }

    getSize() {
        return {
            width: this.canvas.width,
            height: this.canvas.height,
        };
    }

    getMaskData() {
        if (!this._loaded) return null;
        return this.ctx.getImageData(0, 0, this.canvas.width, this.canvas.height);
    }

    setMaskData(maskData) {
        this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);

        if (maskData) {
            this.ctx.putImageData(maskData, 0, 0);
        }
    }

    reset() {
        this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    }

    /**
     * @desc 转换为黑白图并返回图的url
     */
    getImageUrl() {
        const canvas = document.createElement('canvas');
        canvas.width = this.canvas.width;
        canvas.height = this.canvas.height;
        const ctx = canvas.getContext('2d');

        const maskData = this.ctx.getImageData(0, 0, this.canvas.width, this.canvas.height);

        // 遍历MaskData
        const imageData = new ImageData(this.canvas.width, this.canvas.height);
        for (let i = 0; i < maskData.data.length; i += 4) {
            const alpha = maskData.data[i + 3];
            if (alpha > 0) {
                imageData.data[i] = 255; // R
                imageData.data[i + 1] = 255; // G
                imageData.data[i + 2] = 255; // B
                imageData.data[i + 3] = 255; // A
            } else {
                imageData.data[i] = 0; // R
                imageData.data[i + 1] = 0; // G
                imageData.data[i + 2] = 0; // B
                imageData.data[i + 3] = 255; // A
            }
        }

        ctx.putImageData(imageData, 0, 0);
        return canvas.toDataURL();
    }

    isUse() {
        const maskData = this.ctx.getImageData(0, 0, this.canvas.width, this.canvas.height);

        for (let i = 0; i < maskData.data.length; i += 4) {
            const alpha = maskData.data[i + 3];
            if (alpha > 0) {
                return true;
            }
        }
        return false;
    }

    clear() {
        this.reset();
    }
}

export default FJMaskCanvas;
